import { ColumnDef } from '@/components/data-table/DataTable';
import { Tooltip } from '@/components/ui/Tooltip';
import { Copy, EditIcon, Eye, Trash2 } from 'lucide-react';

interface BlogPostColumnsProps {
	onEdit: (post: Blog.BlogPost) => void;
	onDelete: (post: Blog.BlogPost) => void;
	onPreview: (post: Blog.BlogPost) => void;
	onCopyLink: (post: Blog.BlogPost) => void;
}

const formatDate = (iso: string) => {
	const d = new Date(iso);
	if (isNaN(d.getTime())) return '-';
	return d.toLocaleDateString('pt-BR');
};

const BlogPostColumns = ({
	onEdit,
	onDelete,
	onPreview,
	onCopyLink,
}: BlogPostColumnsProps): ColumnDef<Blog.BlogPost, keyof Blog.BlogPost>[] => {
	return [
		{
			header: 'Título',
			accessor: 'title',
			cell: (_, row) => (
				<div className="flex min-w-0 items-center gap-3">
					<div className="h-10 w-16 flex-shrink-0 overflow-hidden rounded-md bg-zinc-100">
						<img
							src={row.banner_url}
							alt={row.title}
							className="h-full w-full object-cover"
							onError={(e) => {
								(e.target as HTMLImageElement).style.display =
									'none';
							}}
						/>
					</div>
					<span className="line-clamp-2 max-w-xs font-medium text-dark">
						{row.title}
					</span>
				</div>
			),
		},
		{
			header: 'Views',
			accessor: 'views',
			cell: (value) => (
				<span className="tabular-nums text-dark">
					{((value as number) ?? 0).toLocaleString('pt-BR')}
				</span>
			),
		},
		{
			header: 'Publicado em',
			accessor: 'created_at',
			cell: (value) => (
				<span className="text-dark">{formatDate(value as string)}</span>
			),
		},
		{
			header: 'Ações',
			accessor: 'id',
			cell: (_, row) => (
				<div className="flex space-x-2">
					<Tooltip
						content="Copiar link"
						position="top"
						className="bg-gray-50 text-dark"
					>
						<button
							onClick={() => onCopyLink(row)}
							className="rounded p-2 text-zinc-500 hover:bg-zinc-100"
						>
							<Copy className="h-4 w-4" />
							<span className="sr-only">Copiar link</span>
						</button>
					</Tooltip>
					<Tooltip
						content="Preview"
						position="top"
						className="bg-gray-50 text-dark"
					>
						<button
							onClick={() => onPreview(row)}
							className="rounded p-2 text-blue-500 hover:bg-blue-50"
						>
							<Eye className="h-4 w-4" />
							<span className="sr-only">Preview</span>
						</button>
					</Tooltip>
					<Tooltip
						content="Editar"
						position="top"
						className="bg-gray-50 text-dark"
					>
						<button
							onClick={() => onEdit(row)}
							className="rounded p-2 text-dark hover:bg-gray-200"
						>
							<EditIcon className="h-4 w-4" />
							<span className="sr-only">Editar</span>
						</button>
					</Tooltip>
					<Tooltip
						content="Deletar"
						position="top"
						className="bg-gray-50 text-dark"
					>
						<button
							onClick={() => onDelete(row)}
							className="rounded p-2 text-red-500 hover:bg-red-100"
						>
							<Trash2 className="h-4 w-4" />
							<span className="sr-only">Deletar</span>
						</button>
					</Tooltip>
				</div>
			),
		},
	];
};

export { BlogPostColumns };
